import React, { useRef } from "react";
import Table from "react-bootstrap/Table";
import { Line } from "react-chartjs-2";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// Import images for PDF header and footer
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const utahData = "/img/headerlogo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

const AeroTable2 = ({ apiData }) => {
  const pdfRef = useRef();

  // Extract years and data preparation
  const years = ["2018", "2019", "2020", "2021", "2022", "2023", "2024"];
  // const chartData = {
  //   labels: years,
  //   datasets: apiData.map((item, index) => ({
  //     label: item.sex_label,
  //     data: years.map((year) => item[year]),
  //     borderColor: `hsl(${(index * 90) % 360}, 70%, 50%)`,
  //      backgroundColor: `hsla(${(index * 90) % 360}, 70%, 50%, 0.3)`,

  //     tension: 0.4,
  //     fill: true,
  //   })),
  // };

  const chartData = {
    labels: years,
    datasets: apiData.map((item) => {
      const isMale = item.sex_label.toLowerCase() === "male";

      return {
        label: item.sex_label,
        data: years.map((year) => item[year]),
        borderColor: isMale ? "rgb(1, 76, 178)" : "rgb(203, 12, 12)", // Male: blue, Female: red
        backgroundColor: isMale ? "rgba(1, 76, 178,1)" : "rgba(203, 12, 12,1)",
        tension: 0.4,
        fill: true,
      };
    }),
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: 'Employment by Sex (2018-2024)',
        font: {
          size: 14,
          weight: 'bold'
        },
        padding: { top: 10, bottom: 10 }
      },
      legend: {
        display: true,
        position: 'top',
        labels: {
          font: { size: 11 },
          boxWidth: 12,
          padding: 10
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Year',
          font: {
            size: window.innerWidth < 768 ? 12 : 14,
            weight: 'bold'
          }
        },
        grid: {
          display: true
        },
        ticks: {
          font: {
            size: window.innerWidth < 768 ? 10 : 12
          }
        }
      },
      y: {
        title: {
          display: true,
          text: 'Number of Employees',
          font: {
            size: window.innerWidth < 768 ? 12 : 14,
            weight: 'bold'
          }
        },
        grid: {
          display: true
        },
        beginAtZero: true,
        ticks: {
          font: {
            size: window.innerWidth < 768 ? 10 : 12
          }
        }
      }
    }
  };

  const downloadPDF = () => {
    const input = pdfRef.current;
    input.style.width = "1100px";

    html2canvas(input, { scale: 3 }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Header
      pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
      pdf.setFontSize(26);
      pdf.text("UTahData.org", 95, 22);
      pdf.line(10, 35, 200, 35);

      // Main Content
      pdf.addImage(imgData, "PNG", 5, 38, imgWidth, imgHeight);

      // Footer
      pdf.line(10, 260, 200, 260);
      // pdf.addImage(utahData, "PNG", 33, 265, 20, 20);
      pdf.addImage(utahData, "PNG", 22, 265, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, 275);
      pdf.addImage(bodex, "PNG", 68, 269, 44, 10);
      pdf.text("using", 112, 275);
      pdf.addImage(kpi, "PNG", 125, 265, 20, 20);
      pdf.text(" 20250916.v3.198", 148, 275);
      const currentDate = new Date().toLocaleDateString();
      pdf.text(`Downloaded on: ${currentDate}`, 150, 295);
      const currentURL = window.location.href;
      pdf.text(`Downloaded from: ${currentURL}`, 10, 295);

      pdf.save("employment-by-sex.pdf");
      input.style.width = "";
    });
  };

  // Calculate summary metrics
  const totalEmployment2024 = apiData.reduce((sum, item) => sum + item["2024"], 0);
  const totalEmployment2018 = apiData.reduce((sum, item) => sum + item["2018"], 0);
  const growthPercent = ((totalEmployment2024 - totalEmployment2018) / totalEmployment2018 * 100).toFixed(1);
  
  const maleData = apiData.find(item => item.sex_label.toLowerCase() === "male");
  const femaleData = apiData.find(item => item.sex_label.toLowerCase() === "female");
  const male2024 = maleData ? maleData["2024"] : 0;
  const female2024 = femaleData ? femaleData["2024"] : 0;
  const malePercent = ((male2024 / totalEmployment2024) * 100).toFixed(1);
  const femalePercent = ((female2024 / totalEmployment2024) * 100).toFixed(1);

  // Find year with highest growth
  let maxGrowth = 0;
  let bestYear = "2018";
  for (let i = 1; i < years.length; i++) {
    const currentYear = years[i];
    const prevYear = years[i-1];
    const currentTotal = apiData.reduce((sum, item) => sum + item[currentYear], 0);
    const prevTotal = apiData.reduce((sum, item) => sum + item[prevYear], 0);
    const growth = ((currentTotal - prevTotal) / prevTotal) * 100;
    if (growth > maxGrowth) {
      maxGrowth = growth;
      bestYear = currentYear;
    }
  }

  return (
    <div className="container">
      <div ref={pdfRef} className="content-to-export p-3 sm:p-4 bg-gradient-to-br from-blue-50 to-white rounded-xl shadow-lg w-full max-w-full overflow-hidden">
        <h3 className="text-xl sm:text-2xl font-bold mb-4 text-center text-gray-800">Employment by Sex (2018-2024)</h3>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-3 mb-4 text-center">
          <div className="p-3 bg-blue-100 rounded-lg shadow-sm">
            <p className="text-xs font-medium text-gray-600 mb-1">
              Total Growth Since 2018
            </p>
            <p className="text-lg font-bold text-blue-800">+{growthPercent}%</p>
          </div>
          <div className="p-3 bg-green-100 rounded-lg shadow-sm">
            <p className="text-xs font-medium text-gray-600 mb-1">
              Best Growth Year
            </p>
            <p className="text-lg font-bold text-green-800">{bestYear} (+{maxGrowth.toFixed(1)}%)</p>
          </div>
          <div className="p-3 bg-purple-100 rounded-lg shadow-sm">
            <p className="text-xs font-medium text-gray-600 mb-1">2024 Total Employment</p>
            <p className="text-lg font-bold text-purple-800">
              {totalEmployment2024.toLocaleString()} 👥
            </p>
          </div>
        </div>

        {/* Gender Distribution Cards */}
        <div className="grid grid-cols-2 gap-3 mb-4 text-center">
          <div className="p-3 bg-blue-100 rounded-lg shadow-sm">
            <p className="text-xs font-medium text-gray-600 mb-1">Male Employment</p>
            <p className="text-lg font-bold text-blue-800">
              {male2024.toLocaleString()} ({malePercent}%)
            </p>
          </div>
          <div className="p-3 bg-pink-100 rounded-lg shadow-sm">
            <p className="text-xs font-medium text-gray-600 mb-1">Female Employment</p>
            <p className="text-lg font-bold text-pink-800">
              {female2024.toLocaleString()} ({femalePercent}%)
            </p>
          </div>
        </div>

        {/* Table + Chart Side-by-Side Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {/* Table */}
          <div className="w-full">
            <div className="overflow-x-auto">
              <Table striped bordered hover responsive className="w-full border-collapse text-center shadow-sm rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-sm">Sex</th>
                    {years.map((year) => (
                      <th key={year} className="p-2 text-sm">{year}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {apiData.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="p-2 text-sm">{item.sex_label}</td>
                      {years.map((year) => (
                        <td key={year} className="p-2 text-sm">{item[year].toLocaleString()}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>

          {/* Chart */}
          <div className="w-full">
            <div className="bg-white rounded-lg shadow-sm p-3">
              <div className="overflow-x-auto">
                <div className="relative" style={{ height: '350px', minHeight: '280px', minWidth: '400px' }}>
                  <Line data={chartData} options={chartOptions} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Download PDF Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="download-btn bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg text-sm"
        >
          <span>Download as PDF</span>
          <img className="w-[1.2rem]" src={pdf} alt="PDF Icon" />
        </button>
      </div>
    </div>
  );
};

export default AeroTable2;
